import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subDays, format } from 'date-fns';
import { getAppInstallationByCompanyApi, type IAppInstallationByCompanyRow } from '@/api/reports.api';
import { DateRangePresetPicker } from '@/components/filters/DateRangePresetPicker';
import { FilterActions } from '@/components/filters/FilterActions';
import { useFilterState } from '@/components/filters/hooks/useFilterState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const UNKNOWN_COMPANY_LABEL = 'Không xác định';

function formatCount(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value);
}

function formatPercent(part: number, total: number): string {
  if (total <= 0) return '—';
  return `${((part / total) * 100).toFixed(1)}%`;
}

function sumTotals(rows: IAppInstallationByCompanyRow[]) {
  return rows.reduce(
    (acc, r) => ({
      totalUsers: acc.totalUsers + r.totalUsers,
      installedUsers: acc.installedUsers + r.installedUsers,
      installedDevices: acc.installedDevices + r.installedDevices,
      androidDevices: acc.androidDevices + r.androidDevices,
      iosDevices: acc.iosDevices + r.iosDevices,
      otherDevices: acc.otherDevices + r.otherDevices,
    }),
    {
      totalUsers: 0,
      installedUsers: 0,
      installedDevices: 0,
      androidDevices: 0,
      iosDevices: 0,
      otherDevices: 0,
    },
  );
}

export function AppInstallationByCompanyPage() {
  const today = useMemo(() => new Date(), []);
  const [range, setRange, resetRange] = useFilterState({
    key: 'report-app-installation-by-company-filters',
    mode: 'localStorage',
    defaultValue: {
      startDate: format(subDays(today, 29), 'yyyy-MM-dd'),
      endDate: format(today, 'yyyy-MM-dd'),
    },
  });

  const hasRange = Boolean(range.startDate && range.endDate);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['reports', 'app-installation-by-company', range.startDate, range.endDate],
    queryFn: () =>
      getAppInstallationByCompanyApi({ from: range.startDate!, to: range.endDate! }),
    enabled: hasRange,
  });

  const totals = useMemo(() => sumTotals(data ?? []), [data]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Cài đặt app theo công ty</h1>
        <p className="text-sm text-muted-foreground">
          Số user cài mới app mobile trong khoảng ngày đã chọn so với tổng user của từng công ty,
          kèm breakdown hệ điều hành thiết bị.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <DateRangePresetPicker
          value={range}
          onChange={(v) => setRange({ startDate: v.startDate ?? '', endDate: v.endDate ?? '' })}
          className="max-w-xs"
          label="Khoảng ngày"
        />
        <FilterActions onReset={resetRange} testIdPrefix="app-installation-filter" />
      </div>

      <div className="rounded-lg border bg-card">
        {!hasRange && (
          <div className="p-6 text-sm text-muted-foreground">Chọn khoảng ngày để xem báo cáo.</div>
        )}

        {hasRange && isLoading && (
          <div className="p-6 text-sm text-muted-foreground">Đang tải...</div>
        )}

        {hasRange && isError && (
          <div className="p-6 text-sm text-destructive">Không tải được dữ liệu báo cáo.</div>
        )}

        {hasRange && !isLoading && !isError && data && data.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">Không có dữ liệu trong khoảng ngày đã chọn.</div>
        )}

        {hasRange && !isLoading && !isError && data && data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Công ty</TableHead>
                <TableHead className="text-right">Tổng user</TableHead>
                <TableHead className="text-right">User cài mới trong kỳ</TableHead>
                <TableHead className="text-right">Thiết bị cài mới trong kỳ</TableHead>
                <TableHead className="text-right">Tỷ lệ cài đặt</TableHead>
                <TableHead className="text-right">Android</TableHead>
                <TableHead className="text-right">iOS</TableHead>
                <TableHead className="text-right">Khác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.companyId} className={isFetching ? 'opacity-60' : undefined}>
                  <TableCell className="font-medium text-foreground">
                    {row.companyName ?? UNKNOWN_COMPANY_LABEL}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatCount(row.totalUsers)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatCount(row.installedUsers)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatCount(row.installedDevices)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatPercent(row.installedUsers, row.totalUsers)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatCount(row.androidDevices)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatCount(row.iosDevices)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatCount(row.otherDevices)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-semibold text-foreground">
                <TableCell>Tổng cộng</TableCell>
                <TableCell className="text-right">{formatCount(totals.totalUsers)}</TableCell>
                <TableCell className="text-right">{formatCount(totals.installedUsers)}</TableCell>
                <TableCell className="text-right">{formatCount(totals.installedDevices)}</TableCell>
                <TableCell className="text-right">
                  {formatPercent(totals.installedUsers, totals.totalUsers)}
                </TableCell>
                <TableCell className="text-right">{formatCount(totals.androidDevices)}</TableCell>
                <TableCell className="text-right">{formatCount(totals.iosDevices)}</TableCell>
                <TableCell className="text-right">{formatCount(totals.otherDevices)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
